import { useState, useEffect } from 'react';
import { X, Phone, Calendar, ArrowRight } from 'lucide-react';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { toast } from 'sonner';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export const LeadCapturePopup = () => {
  const [visible, setVisible] = useState(false);
  const [dismissed, setDismissed] = useState(false);
  const [loading, setLoading] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [formData, setFormData] = useState({ name: '', email: '', phone: '' });

  useEffect(() => {
    // Don't show on admin pages
    if (window.location.pathname.startsWith('/admin')) return;
    // Check if already dismissed this session
    if (sessionStorage.getItem('popup_dismissed')) return;

    const timer = setTimeout(() => {
      if (!dismissed) setVisible(true);
    }, 15000); // 15 seconds

    return () => clearTimeout(timer);
  }, [dismissed]);

  const handleDismiss = () => {
    setVisible(false);
    setDismissed(true);
    sessionStorage.setItem('popup_dismissed', 'true');
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await axios.post(`${API}/leads`, {
        ...formData,
        source: 'popup',
        message: 'Lead capture popup submission'
      });
      setSubmitted(true);
      toast.success("Thank you! We'll be in touch soon.");
      setTimeout(() => handleDismiss(), 3000);
    } catch {
      toast.error("Something went wrong. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  if (!visible) return null;

  return (
    <div className="fixed inset-0 z-[100] flex items-center justify-center bg-black/60 backdrop-blur-sm p-4" data-testid="lead-capture-popup">
      <div className="bg-[#111111] border border-[#D4AF37]/30 rounded-xl max-w-md w-full overflow-hidden shadow-2xl shadow-[#D4AF37]/10 animate-fade-in-up">
        {/* Close */}
        <button
          onClick={handleDismiss}
          className="absolute top-4 right-4 text-[#808080] hover:text-[#F5F5F0] z-10 p-1"
          data-testid="popup-close-btn"
        >
          <X className="w-5 h-5" />
        </button>

        {/* Gold accent bar */}
        <div className="h-1 bg-gradient-to-r from-[#D4AF37] via-[#F5E6A3] to-[#D4AF37]" />

        <div className="p-6 sm:p-8 relative">
          {submitted ? (
            <div className="text-center py-4">
              <div className="w-16 h-16 rounded-full bg-[#D4AF37]/10 flex items-center justify-center mx-auto mb-4">
                <ArrowRight className="w-8 h-8 text-[#D4AF37]" />
              </div>
              <h3 className="text-xl text-[#F5F5F0] mb-2" style={{ fontFamily: 'Playfair Display' }}>
                We'll Be In Touch!
              </h3>
              <p className="text-[#808080] text-sm">Expect a call or email within 24 hours.</p>
            </div>
          ) : (
            <>
              <div className="text-center mb-6">
                <h3 className="text-2xl text-[#F5F5F0] mb-2" style={{ fontFamily: 'Playfair Display' }}>
                  Get a <span className="text-gold-gradient">Free Consultation</span>
                </h3>
                <p className="text-[#808080] text-sm">
                  Whether buying, selling, or investing — let's talk about your goals.
                </p>
              </div>

              <form onSubmit={handleSubmit} className="space-y-3">
                <Input
                  type="text"
                  placeholder="Your Name *"
                  value={formData.name}
                  onChange={(e) => setFormData({...formData, name: e.target.value})}
                  required
                  className="bg-[#1A1A1A] border-[#333] text-[#F5F5F0] placeholder:text-[#808080]"
                  data-testid="popup-name-input"
                />
                <Input
                  type="email"
                  placeholder="Email *"
                  value={formData.email}
                  onChange={(e) => setFormData({...formData, email: e.target.value})}
                  required
                  className="bg-[#1A1A1A] border-[#333] text-[#F5F5F0] placeholder:text-[#808080]"
                  data-testid="popup-email-input"
                />
                <Input
                  type="tel"
                  placeholder="Phone"
                  value={formData.phone}
                  onChange={(e) => setFormData({...formData, phone: e.target.value})}
                  className="bg-[#1A1A1A] border-[#333] text-[#F5F5F0] placeholder:text-[#808080]"
                  data-testid="popup-phone-input"
                />
                <Button type="submit" className="btn-gold w-full py-3 font-medium" disabled={loading} data-testid="popup-submit-btn">
                  {loading ? 'Sending...' : 'Get Free Consultation'}
                </Button>
              </form>

              <div className="mt-4 flex items-center justify-center gap-4">
                <a href="tel:7863560878" className="text-[#D4AF37] text-sm flex items-center hover:underline" data-testid="popup-call-link">
                  <Phone className="w-3.5 h-3.5 mr-1" /> Call Now
                </a>
                <span className="text-[#333]">|</span>
                <a href="https://calendly.com/seantherealtor/15min" target="_blank" rel="noopener noreferrer" className="text-[#D4AF37] text-sm flex items-center hover:underline" data-testid="popup-book-link">
                  <Calendar className="w-3.5 h-3.5 mr-1" /> Book Appointment
                </a>
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
};
